import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { AuthContext } from '@dashboard/context/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';

import { fetchSetupIntent, editPaymentMethod } from './helpers';
import FormSubmit from '@dashboard/components/Form/components/FormSubmit';
import { FormElement } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { stripePublicKey } from '@dashboard/services/env';

const createStyle = () => ({
  style: {
    base: {
      iconColor: '#c0b6ba',
      fontWeight: 400,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
      fontSmoothing: 'antialiased',
      background: 'white',
      fontSize: '16px',
      lineHeight: '1.5',
      padding: '8px',
      color: '#382b2f', // text-gray-800
      '::placeholder': {
        color: '#c0b6ba',
      },
      '::focus-visible': {
        outline: 'none',
      },
    },
  },
});

const PaymentMethodEditForm = ({ setEditing }) => {
  const { sellerId, token } = useContext(AuthContext);
  const stripe = useStripe();
  const elements = useElements();

  const [setupIntent, setSetupIntent] = useState(null);
  const [setupIntentError, setSetupIntentError] = useState('');
  const [isSettingUpIntent, setIsSettingUpIntent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingError, setEditingError] = useState('');

  useEffect(() => {
    async function createIntent() {
      setIsSettingUpIntent(true);
      try {
        const { data, error } = await fetchSetupIntent(sellerId, token);
        if (!data || error) throw new Error(error);
        setSetupIntent(data);
      } catch (error) {
        setSetupIntentError(error.message || 'Unable to edit payment method.');
      } finally {
        setIsSettingUpIntent(false);
      }
    }

    if (!setupIntent && !isSettingUpIntent) {
      createIntent();
    }
  }, [setupIntent, sellerId, token]);

  const submit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const cardElement = elements.getElement(CardElement);
      const { setupIntent: intent, error: cardSetupError } = await stripe.confirmCardSetup(
        setupIntent.client_secret,
        { payment_method: { card: cardElement } },
      );

      if (!intent || cardSetupError) {
        throw new Error(cardSetupError?.message);
      }

      const { data, error } = await editPaymentMethod({ intent }, sellerId, token);
      if (!data || error) {
        throw new Error(error);
      }
      setIsSubmitting(false);
      setEditing(false);
    } catch (error) {
      setIsSubmitting(false);
      setEditingError(error.message || 'Unable to save new payment method');
    }
  };

  if (setupIntentError) {
    return <div>{setupIntentError}</div>;
  }

  return (
    <form onSubmit={submit}>
      <FormElement>
        <CardElement
          options={createStyle()}
          className="bg-white placeholder-gray-200 border-none shadow-input focus:shadow-input-focus appearance-none block w-full py-2 px-2 rounded"
        />
      </FormElement>

      <FormErrorBase>{editingError}</FormErrorBase>

      <FormSubmit>
        {isSubmitting && <Spinner />}
        {!isSubmitting && (
          <>
            <Button type="submit" appearance="primary">
              Save
            </Button>
            <Button
              appearance="secondary"
              onClick={() => setEditing(false)}
              type="button"
              className="ml-2"
            >
              Cancel
            </Button>
          </>
        )}
      </FormSubmit>
    </form>
  );
};

PaymentMethodEditForm.propTypes = {
  setEditing: PropTypes.func.isRequired,
};

const PaymentMethodEdit = props => (
  <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
    <PaymentMethodEditForm {...props} />
  </Elements>
);

export default PaymentMethodEdit;
